/**
 * pages/AdminHome.tsx — the administration console's landing page.
 *
 * **Why a card grid earns its keep, and is not decoration.** The section strip above it can only *name*
 * a section. The question an administrator actually arrives with is "where do I revoke someone's
 * access?", and only prose answers it — which is why the Access card's sentence ends with the words
 * *this is where access is taken away*. The sentence and the way in are one target.
 *
 * Every card is one of the sections the caller actually holds — never the whole table with the rest
 * greyed out. `02-patterns.md` §2.4 and `06-modules.md` §1.7 both forbid enumerating destinations a
 * caller could never reach, and a disabled card is an enumeration with an apology attached.
 *
 * The icons are the four already in `ICONS`, so a section reads as the same thing on the card, in the
 * strip and (for the console as a whole) in the primary navigation. `03-components.md` §7 forbids a
 * whole-card link containing further interactive elements, so the card carries none: it is one link.
 */
import { blurbFor, type AdminConsoleSection, type AdminSectionSpec } from "../lib/admin";
import { ICONS } from "../components/Shell";
import { Page } from "../components/ui";

/** Which glyph belongs to which section. Explicit, because the two vocabularies are not the same words. */
const ICON_FOR: Readonly<Record<AdminConsoleSection, keyof typeof ICONS>> = {
  people: "people",
  access: "access",
  "business-units": "businessUnits",
  audit: "audit",
};

export interface AdminHomeProps {
  readonly sections: readonly AdminSectionSpec[];
  /** The active unit's display name, or null when none is chosen. Only the scoped card says anything. */
  readonly activeScopeName: string | null;
  /**
   * Whether this reader may change anything at all here — the superuser, and nobody else.
   *
   * The card's sentence is chosen by it. A delegate reads what the section lets THEM do; promising them
   * the verbs and then showing *View only* on arrival is the button lie in prose, and it is the reason
   * this prop exists rather than the page picking `spec.blurb` unconditionally.
   */
  readonly canChange: boolean;
}

export function AdminHome({ sections, activeScopeName, canChange }: AdminHomeProps): JSX.Element {
  return (
    <Page
      title="Administration"
      lede={
        canChange
          ? "Who may use this application, what each of them may do, where it comes from, and how to change it."
          : "Who may use this application, what each of them may do, and where it comes from. Changing any of it is the superuser's."
      }
    >
      <div className="admin-cards">
        {sections.map((spec) => (
          <a className="admin-card" key={spec.key} href={spec.href}>
            <span className="admin-card__icon" aria-hidden="true">
              {ICONS[ICON_FOR[spec.key]]}
            </span>
            <h2 className="admin-card__title">{spec.label}</h2>
            <p className="admin-card__note">{blurbFor(spec, canChange)}</p>
            {/* Audit alone. Said on the card because arriving at a screen that asks for a business unit
                is a surprise; being told before you go is not. */}
            {spec.scoped ? (
              <p className="admin-card__scope">
                {activeScopeName === null ? "Needs a business unit" : `Scoped to ${activeScopeName}`}
              </p>
            ) : null}
          </a>
        ))}
      </div>
    </Page>
  );
}
